import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useDrag, DragElementWrapper } from "react-dnd";
import { CSSTransition } from "react-transition-group";
import classnames from "classnames";

import { State } from "../../redux/store";
import * as phonemeActions from "../../redux/actions/phoneme";
import styles from "./phoneme.module.css";
import { srcPath } from "../../util/Path";
import { dndItemTypes } from "../../react-dnd/DndConfig";

const inBasketItemStyle = (index: number, length: number): any => {
  const column = Math.floor(index / 3);
  const row = index % 3;
  const rowLength = (column + 1) * 3 < length ? 3 : length % 3 || 3;

  const top = column * -170 + 140;
  const left = (rowLength - 1) * -0.5 + row;

  return { top: `${top}px`, left: `calc(50% + ${left * 180 - 80}px)` };
};

const outBasketItemStyle = (index: number): any => {
  const row = Math.floor(index / 2);
  const column = index % 2 === 1 ? 1 : -1;

  const top = row * 175;
  const left = column * 460;

  return { top: `${top}px`, left: `calc(50% + ${left - 80}px)` };
};

interface Filled {
  dragRef: DragElementWrapper<any>;
  word: string;
  outIndex: number;
  isDragging: boolean;
  style?: any;
}

interface Props {
  word: string;
  outIndex: number;
  itemLoaded: boolean;
}

export const BasketItemFilled = (props: Filled) => {
  const dispatch = useDispatch();
  const { dragRef, word, outIndex, isDragging, style } = props;

  const { grade, unit, game } = useSelector((state: State) => state.shared);
  const { inBasket, basketResponse, basketChecking } = useSelector(
    (state: State) => state.phoneme
  );

  const [imgErrors, setImgErrors] = useState([]);
  const [responseImg, setResponseImg] = useState<string>(null);

  const inIndex = inBasket.indexOf(word);
  const isInBasket = inIndex !== -1;

  let response: string;
  if (basketResponse && basketResponse.index === inIndex) {
    response = basketResponse.response;
  }

  const selectItem = () => {
    if (!isInBasket && !basketChecking) {
      dispatch(phonemeActions.selectBasketItem(word));
    }
  };

  useEffect(() => {
    if (response) setResponseImg(response);
  }, [setResponseImg, response]);

  return (
    <div
      ref={dragRef}
      className={classnames(styles.basketItem)}
      style={{
        ...(isInBasket
          ? inBasketItemStyle(inIndex, inBasket.length)
          : outBasketItemStyle(outIndex)),
        cursor: isInBasket || basketChecking ? "default" : "move",
        transition: isDragging
          ? "none"
          : "transform 500ms, top 800ms ease-in-out, left 800ms ease-in-out",
        ...style,
      }}
      onClick={selectItem}
    >
      <img
        className={styles.basketItemImg}
        style={{ display: imgErrors.includes(outIndex) ? "none" : "block" }}
        src={srcPath(`phoneme/word_icons/${grade}.${unit}.${game}/${word}.png`)}
        alt=""
        onError={() => setImgErrors((prev) => [...prev, outIndex])}
      />
      {imgErrors.includes(outIndex) && (
        <div className={styles.basketItemImgPlaceholder}>{word}</div>
      )}
      <CSSTransition
        in={!!response}
        timeout={200}
        mountOnEnter
        unmountOnExit
        classNames={{
          enter: styles.basketResponseEnter,
          enterActive: styles.basketResponseEnterActive,
          exit: styles.basketResponseExit,
          exitActive: styles.basketResponseExitActive,
        }}
      >
        <img
          className={styles.basketResponse}
          src={srcPath(`shared/answer_${response || responseImg}.png`)}
          alt=""
        />
      </CSSTransition>
    </div>
  );
};

const BasketItem = (props: Props) => {
  const { word, outIndex, itemLoaded } = props;

  const { inBasket, basketChecking } = useSelector(
    (state: State) => state.phoneme
  );

  const inIndex = inBasket.indexOf(word);
  const isInBasket = inIndex !== -1;

  const [{ isDragging }, drag] = useDrag({
    item: {
      type: dndItemTypes.PHONEME_BASKET_ITEM,
      word,
    },
    collect: (monitor) => ({
      isDragging: !!monitor.isDragging(),
    }),
  });

  if (!isDragging) {
    return (
      <CSSTransition
        in={!itemLoaded && !isInBasket}
        appear
        timeout={{
          appear: 500 + outIndex * 50,
        }}
        classNames={{
          appear: styles.basketItemAppear,
          appearDone: styles.basketItemAppearDone,
        }}
      >
        <BasketItemFilled
          dragRef={isInBasket || basketChecking ? null : drag}
          word={word}
          outIndex={outIndex}
          isDragging={isDragging}
        />
      </CSSTransition>
    );
  }

  return <div className={styles.basketItemEmpty}></div>;
};

export default BasketItem;
